import React, { useCallback, useRef, useState, useLayoutEffect} from "react";
import ChatPanel from "../../search/components/ChatPanel";
import ReviewCard from "../../../features/review/ReviewCard";
import ReviewCardSkeleton from "../../../features/review/ReviewCardSkeleton";
import { useChatSearch } from "../../search/hooks/useChatSearch";
import type { ChatMsg } from "../../search/components/ChatPanel";
import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels";
import { useAiCompareSSE } from "../../search/hooks/useAiCompareSSE";
import { CompareMarkdownView } from "../components/CompareMarkdownView";
import PixelHumanWalkers from "../components/PixelHuman";
import { useLocation } from "react-router-dom";
import { Button, useMediaQuery, useTheme } from "@mui/material";


type Props = {
  keyword: string;
  setKeyword: React.Dispatch<React.SetStateAction<string>>;
};

const SearchResultsPage: React.FC<Props> = ({ keyword }) => {
  const [activeTab, setActiveTab] = useState<"list" | "compare">("list");
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const keywordFromQuery = params.get("keyword") ?? "";

  const baseKeyword = keywordFromQuery || keyword || "";
  const initialText = useRef(baseKeyword || "キャンプ用品を探したい").current;

  const {
      messages,
      chips,
      cards,
      isLoading,
      hasShownFirstList,
      selectedChips,
      handleUserMessage,
      handleChipSelect,
  } = useChatSearch(initialText);


  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const { blocks, loading, done, startCompare } = useAiCompareSSE();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobilePane, setMobilePane] = useState<"chat" | "results">("chat");

  const buildAmazonUrl = () => {
      const base = initialText.trim();
      const conds = selectedChips.map((v) => v.trim()).filter(Boolean);
      const q = [base, ...conds].filter(Boolean).join(" ").trim();
      if (!q) return "";
      return `https://www.amazon.co.jp/s?k=${encodeURIComponent(q)}&tag=afrofair-22`;
  };


  const handleSelectChange = useCallback((id: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleCompare = () => {
    if (selectedIds.size < 2) {
      alert("比較する商品を2つ以上選んでください");
      return;
    }
    setActiveTab("compare");
    startCompare(Array.from(selectedIds), cards);

    if (isMobile) setMobilePane("results");
  };

  const actionBarRef = useRef<HTMLDivElement | null>(null);
  const [actionBarH, setActionBarH] = useState(0);

  const raf1Ref = useRef<number | null>(null);
  const raf2Ref = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!isMobile) return;
    if (activeTab !== "list") return;

    void mobilePane;

  const el = actionBarRef.current;
  if (!el) return;

  const measure = () => {
    const h = el.getBoundingClientRect().height;
    if (h) setActionBarH(h);
  };

  const ro = new ResizeObserver(() => {
    // レイアウト確定後に計測（iOSで効く）
    if (raf1Ref.current != null) cancelAnimationFrame(raf1Ref.current);
    raf1Ref.current = requestAnimationFrame(() => {
      measure();
      if (raf2Ref.current != null) cancelAnimationFrame(raf2Ref.current);
      raf2Ref.current = requestAnimationFrame(measure);
    });
  });

  ro.observe(el);

  // 初回も同じく2回測る
  measure();
  raf1Ref.current = requestAnimationFrame(() => {
    measure();
    raf2Ref.current = requestAnimationFrame(measure);
  });

  return () => {
    ro.disconnect();
    if (raf1Ref.current != null) cancelAnimationFrame(raf1Ref.current);
    if (raf2Ref.current != null) cancelAnimationFrame(raf2Ref.current);
    raf1Ref.current = null;
    raf2Ref.current = null;
  };
}, [isMobile, activeTab, mobilePane]);

  // =========================
  // 右ペイン（商品結果）部分を関数化
  // =========================
  const ResultsPane = (
    <div className="w-full h-full bg-[#f5f7fb] flex flex-col overflow-hidden">
      {/* スマホ時だけ「チャットに戻る」を出す */}
      {isMobile && (
        <div className="p-2 bg-[#f5f7fb]">
          <Button
            fullWidth
            variant="contained"
            onClick={() => setMobilePane("chat")}
            sx={{ borderRadius: 2, py: 1.5 }}
          >
            チャットに戻る
          </Button>
        </div>
      )}

      {/* タブ＋比較ボタンのヘッダ */}
      <div className={`${isMobile ? "p-2" : "p-3"} bg-gray-50 border-b`}>
        <div className="flex items-center justify-between">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab("list")}
              className={`
                ${isMobile ? "px-2 py-1 text-sm" : "px-3 py-2"}
                rounded-t-md transition
                ${activeTab === "list"
                  ? "font-bold border-b-2 border-blue-600 text-blue-600 bg-white"
                  : "text-gray-500 hover:text-gray-700"}
              `}
            >
              リスト
            </button>

            <button
              onClick={() => setActiveTab("compare")}
              className={`
                ${isMobile ? "px-2 py-1 text-sm" : "px-3 py-2"}
                rounded-t-md transition
                ${activeTab === "compare"
                  ? "font-bold border-b-2 border-blue-600 text-blue-600 bg-white"
                  : "text-gray-500 hover:text-gray-700"}
              `}
            >
              比較（{selectedIds.size}）
            </button>
          </div>

          {/* PC時は右側にアクションを表示 */}
          {!isMobile && activeTab === "list" && (
            <div className="flex items-center gap-3">
              <a
                href={buildAmazonUrl() || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className={`
                  px-4 py-2 rounded shadow-lg transition border
                  ${buildAmazonUrl()
                    ? "bg-white text-gray-800 hover:bg-gray-100 border-gray-300"
                    : "bg-gray-200 text-gray-400 border-gray-200 pointer-events-none"}
                `}
              >
                Amazonで見る
              </a>

              <button
                onClick={handleCompare}
                className="px-4 py-2 bg-blue-600 text-white rounded shadow-lg hover:bg-blue-700 transition"
              >
                比較する
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        className="min-w-0 min-h-0 flex-1 overflow-y-auto p-3 bg-gray-50">
        {activeTab === "list" && (
          <>
            {!hasShownFirstList ? (
              <div>
                <div className="flex items-center gap-2 text-gray-500 mb-4 px-1">
                  <span className="animate-pulse">商品を探しています</span>
                  <span className="animate-bounce">.</span>
                  <span className="animate-bounce [animation-delay:0.1s]">.</span>
                  <span className="animate-bounce [animation-delay:0.2s]">.</span>
                </div>

                <ReviewCardSkeleton />
                <ReviewCardSkeleton />
                <ReviewCardSkeleton />
                <ReviewCardSkeleton />
                <ReviewCardSkeleton />
              </div>
            ) : cards.length === 0 ? (
              <p className="text-gray-500 mt-6">まだ商品が見つかっていません。</p>
            ) : (
              <>
                {cards.map((item, index) => (
                  <ReviewCard
                    key={item.id}
                    item={item}
                    index={index}
                    selected={selectedIds.has(item.id)}
                    onSelectChange={handleSelectChange}
                  />
                ))}
                {/* 下部固定ボタン分の余白（スマホのみ） */}
                {isMobile && activeTab === "list" && (
                  <div style={{ height: `calc(${actionBarH}px + env(safe-area-inset-bottom))` }} />
                )}
              </>
            )}
          </>
        )}

        {activeTab === "compare" && (
          <div className="h-full overflow-y-auto p-4 bg-gray-50">
            {loading && blocks.length === 0 && (
              <div className="text-gray-500 mb-4">比較結果を生成しています…</div>
            )}

            <div className="flex items-start gap-3 bg-white p-6 rounded-lg shadow">
              <div className="w-8 h-8 bg-gray-200 rounded-full" />
              <div className="prose prose-slate max-w-none">
                <div className="prose prose-slate max-w-none">
                  {blocks.map((block, i) => (
                    <CompareMarkdownView key={i} markdown={block} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* スマホだけ下固定アクションバー */}
      {isMobile && activeTab === "list" && (
        <div
          ref={actionBarRef}
          className="fixed left-0 right-0 bottom-0 z-50 bg-[#f5f7fb] border-t p-2"
          style={{ paddingBottom: "calc(4px + env(safe-area-inset-bottom))" }}
        >
          <div className="flex gap-2">
            <a
              href={buildAmazonUrl() || undefined}
              target="_blank"
              rel="noopener noreferrer"
              className={`
                flex-1 text-center
                px-3 py-2 text-sm
                rounded-lg border shadow-sm transition
                ${buildAmazonUrl()
                  ? "bg-white text-gray-800 hover:bg-gray-100 border-gray-300"
                  : "bg-gray-200 text-gray-400 border-gray-200 pointer-events-none"}
              `}
            >
              Amazon
            </a>

            <button
              onClick={handleCompare}
              disabled={selectedIds.size < 2}
              className={`
                flex-1
                px-3 py-2 text-sm
                rounded-lg shadow transition
                ${selectedIds.size >= 2
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"}
              `}
            >
              比較 {selectedIds.size}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // =========================
  // スマホ：1ペイン切替（画像の挙動）
  // =========================
  const shouldShowInitialLoading =
    isMobile &&
    mobilePane === "chat" &&
    !hasShownFirstList &&
    cards.length === 0 &&
    (messages.length > 0 || baseKeyword.trim().length > 0);

  const shouldShowReloadLoading =
  isMobile &&
  mobilePane === "chat" &&
  hasShownFirstList &&
  cards.length > 0 &&
  isLoading;

  if (isMobile) {
    return (
      <div className="w-full h-full bg-[#f5f7fb] overflow-hidden">
        {mobilePane === "chat" ? (
          <ChatPanel
            messages={messages as ChatMsg[]}
            onSend={handleUserMessage}
            belowMessages={
              <>
                {/* ▼ スマホ：初回検索中のローディング（PCには影響なし） */}
                {shouldShowInitialLoading && (
                  <div className="mt-3">
                    <div className="flex items-center gap-2 text-gray-600 mb-2 px-1">
                      <span className="font-semibold">検索中です</span>
                      <span className="animate-bounce">.</span>
                      <span className="animate-bounce [animation-delay:0.1s]">.</span>
                      <span className="animate-bounce [animation-delay:0.2s]">.</span>
                    </div>
                  </div>
                )}

                {/* ▼ スマホ：2回目以降のローディング（カードが残ってる時） */}
                {shouldShowReloadLoading && (
                  <div className="mt-3 px-1">
                    <div className="flex items-center gap-2 text-gray-600 mb-2">
                      <span className="font-semibold text-sm">更新中</span>
                      <span className="animate-bounce">.</span>
                      <span className="animate-bounce [animation-delay:0.1s]">.</span>
                      <span className="animate-bounce [animation-delay:0.2s]">.</span>
                    </div>
                  </div>
                )}

                {/* ▼ チップ（検索が落ち着いたら表示） */}
                {hasShownFirstList && chips.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {chips.map((chip, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleChipSelect(chip)}
                        className="px-4 py-1.5 text-sm border border-gray-300 rounded-full bg-white hover:bg-gray-100 transition"
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                )}

                {/* ▼ 結果が出たら「おすすめ商品を見てみる」 */}
                {hasShownFirstList && cards.length > 0 && (
                  <div className="mt-4">
                    <button
                      onClick={() => setMobilePane("results")}
                      className="w-full px-4 py-3 bg-blue-700 text-white rounded-lg shadow hover:bg-blue-800 transition"
                    >
                      おすすめ商品を見てみる
                    </button>
                  </div>
                )}
              </>
            }
          />
        ) : (
          ResultsPane
        )}
      </div>
    );
  }

  // =========================
  // PC：左右2ペイン
  // =========================
  return (
    <PanelGroup direction="horizontal" className="w-full h-full bg-[#f5f7fb]">
      {/* 左：チャット */}
      <Panel defaultSize={35} minSize={20}>
        <ChatPanel
          messages={messages as ChatMsg[]}
          onSend={handleUserMessage}
          belowMessages={
            isLoading ? (
              <PixelHumanWalkers  />
            ) : chips.length > 0 ? (
              <div className="flex flex-wrap gap-2 mt-3">
                {chips.map((chip, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleChipSelect(chip)}
                    className="px-4 py-1.5 text-sm border border-gray-300 rounded-full bg-white hover:bg-gray-100 transition"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            ) : null
          }
        />
      </Panel>
      {/* 可変 */}
      <PanelResizeHandle className="w-1 bg-gray-300 hover:bg-gray-400 cursor-col-resize" />
      {/* 右：商品結果 */}
      <Panel defaultSize={65} minSize={30}>
        {ResultsPane}
      </Panel>
    </PanelGroup>
  );
};

export default SearchResultsPage;
